import React, { useContext, useEffect } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { customFilterOptions } from "@ts/constants";
import {
    ChangeInValueCellRenderer,
    CheckboxCellRenderer,
    FilterTextFormatter,
    ProgramTypeCellRenderer,
    StatusCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import { mainContext } from "@ts/constants";
import { numericValueFormatter } from "@ts/utils";
import { useSelector } from "react-redux";
import { onProductSelectionChanged } from "@ts/performance-overview";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import {
    ICustomCellRendererParams,
    IMainContext,
    IProducts,
    ParentModule,
    SubModule,
} from "@ts/common-types";
import { useGetModulesData, useUpdateModule } from "@hooks/AllCustomHooks";

const Products = React.memo(() => {
    const {
        productsLoading,
        productsGridApi,
        setProductsGridApi
    } = useContext<IMainContext>(mainContext);

    const productsData = useSelector(
        (state: IProducts) => state.productsData.value
    );
    const productsColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "sku_title",
                headerName: "Product",
                minWidth: 350,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                tooltipField: "sku_title",
                checkboxSelection: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                headerCheckboxSelection: true,
                // valueFormatter: (params) => {
                //   return titleCase(params.value || "");
                // },
                cellRenderer: CheckboxCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
            },
            {
                field: "state",
                headerName: "Status",
                maxWidth: 100,
                minWidth: 180,
                filter: true,
                tooltipField: "state",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                cellRenderer: (params: ICustomCellRendererParams) =>
                    StatusCellRenderer(params, "products"),
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "ad_group_name",
                headerName: "Ad Group",
                minWidth: 150,
                filter: "agTextColumnFilter",
                tooltipField: "ad_group_name",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "campaign_name",
                headerName: "Campaign",
                filter: "agTextColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "portfolio_name",
                headerName: "Portfolio",
                filter: "agTextColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "program_type",
                headerName: "Ad Type",
                filter: "agSetColumnFilter",
                cellRenderer: ProgramTypeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter,
                },
            },
            {
                headerName: "Impressions",
                cellStyle: { whiteSpace: "normal" },
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
                hide: true,
            },
            {
                headerName: "Clicks",
                cellStyle: { whiteSpace: "normal" },
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
                hide: true,
            },
            {
                headerName: "Spends",
                cellStyle: { whiteSpace: "normal" },
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
                hide: true,
            },
            {
                headerName: "Indirect Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_view_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                cellStyle: { whiteSpace: "normal" },
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPC",
                cellStyle: { whiteSpace: "normal" },
                field: "cpc",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "AOV",
                cellStyle: { whiteSpace: "normal" },
                field: "aov",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                    hideInfinite: true
                },
            },
            {
                headerName: "CR",
                cellStyle: { whiteSpace: "normal" },
                field: "cr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "CPA",
                cellStyle: { whiteSpace: "normal" },
                field: "cpa",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "ACoS",
                cellStyle: { whiteSpace: "normal" },
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },

            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPM",
                field: "cpm",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "RoAS",
                cellStyle: { whiteSpace: "normal" },
                field: "roas",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Top ASIN",
                cellStyle: { whiteSpace: "normal" },
                field: "top_product",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "% Spend",
                cellStyle: { whiteSpace: "normal" },
                field: "percent_spend",
                hide: true,
            },
            {
                field: "portfolio_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sys_campaign_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sys_adgroup_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "asin_id",
                filter: "agSetColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "ad_id",
                filter: "agSetColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
        ],
    });

    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.PRODUCT,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });

    console.log("gridParams", gridParams);

    const updateModule = useUpdateModule("products");

    useEffect(() => {
        sessionStorage.setItem("trendType", "products");
    }, []);

    return (
        <>
            <div className="wrapper" style={{ flex: "1 1 0" }}>
                <div
                    id="products-grid"
                    className="ag-theme-alpine"
                    style={{ height: "100%", width: "100%" }}
                >
                    <AgGridLayout
                        setGridApi={setProductsGridApi}
                        columnDefs={productsColumnDefs}
                        rowData={productsData}
                        updateModule={updateModule}
                        loading={productsLoading}
                        onSelectionChanged={onProductSelectionChanged}
                        isTotalRowsReq={true}
                        rowHeight={gridParams?.rowHeight}
                    />
                </div>
                <AddRulesModal api={productsGridApi} />
            </div>
        </>
    );
});

export default Products;
